'use client';
import React, { useState, useMemo } from 'react';
import { Search, Filter, Calendar, ChevronDown, AlertTriangle, TrendingUp, Users, DollarSign, Activity, RefreshCw } from 'lucide-react';
import { DISTRIBUTORS, GSV_TREND_DATA, GENERATION_DATA, Rank, Distributor } from '@/data/mockData';
import NetworkTree from './NetworkTree';
import CenterPerformancePanel from './CenterPerformancePanel';
import CommissionRightPanel from './CommissionRightPanel';
import GenerationBottomPanel from './GenerationBottomPanel';
import AlertsBottomPanel from './AlertsBottomPanel';

const RANKS: Rank[] = [
  'Distributor', 'Brand Partner Gold', 'Brand Partner Lapis', 'Brand Partner Ruby',
  'Executive', 'Provisional Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director',
];

export default function AdminDashboardClient() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRank, setSelectedRank] = useState<string>('All');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [maxDepth, setMaxDepth] = useState<number>(6);
  const [selectedPeriod, setSelectedPeriod] = useState('Apr 2026');
  const [selectedDistributorId, setSelectedDistributorId] = useState<string>('dist-001');
  const [showRankFilter, setShowRankFilter] = useState(false);

  const allDistributors = Object.values(DISTRIBUTORS);

  const filteredDistributors = useMemo(() => {
    return allDistributors.filter(d => {
      const matchSearch = searchQuery === '' ||
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchRank = selectedRank === 'All' || d.rank === selectedRank;
      const matchActive = activeFilter === 'all' || (activeFilter === 'active' ? d.active : !d.active);
      return matchSearch && matchRank && matchActive;
    });
  }, [searchQuery, selectedRank, activeFilter, allDistributors]);

  const selectedDist = DISTRIBUTORS[selectedDistributorId] || DISTRIBUTORS['dist-001'];

  const totalGSV = allDistributors.reduce((s, d) => s + d.gsv, 0);
  const activeCount = allDistributors.filter(d => d.active).length;
  const totalComm = allDistributors.reduce((s, d) => s + d.commissionThisMonth, 0);
  const pexecCount = allDistributors.filter(d => d.rank === 'Provisional Executive').length;
  const atRiskCount = allDistributors.filter(d => d.active && d.psv < 100).length + pexecCount;

  const periods = ['Jan 2026', 'Feb 2026', 'Mar 2026', 'Apr 2026'];

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Top Filter Bar */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-1.5 w-52">
            <Search size={14} className="text-zinc-500 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search distributor..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="bg-transparent text-sm text-zinc-200 placeholder-zinc-600 outline-none w-full"
            />
          </div>
        </div>

        {/* Rank Filter */}
        <div className="relative">
          <button
            onClick={() => setShowRankFilter(!showRankFilter)}
            className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-300 hover:border-zinc-600 transition-colors"
          >
            <Filter size={13} className="text-zinc-500" />
            <span>{selectedRank === 'All' ? 'All Ranks' : selectedRank}</span>
            <ChevronDown size={13} className="text-zinc-500" />
          </button>
          {showRankFilter && (
            <div className="absolute top-full mt-1 left-0 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 w-52 animate-fade-in">
              {['All', ...RANKS].map(r => (
                <button
                  key={`rank-filter-${r}`}
                  onClick={() => { setSelectedRank(r); setShowRankFilter(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-700 transition-colors ${selectedRank === r ? 'text-violet-300' : 'text-zinc-300'}`}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Active Filter */}
        <div className="flex items-center gap-1 bg-zinc-800/40 border border-zinc-700 rounded-lg p-0.5">
          {(['all', 'active', 'inactive'] as const).map(f => (
            <button
              key={`active-filter-${f}`}
              onClick={() => setActiveFilter(f)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 ${
                activeFilter === f ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Depth */}
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>Depth:</span>
          {[1,2,3,4,5,6].map(d => (
            <button
              key={`depth-${d}`}
              onClick={() => setMaxDepth(d)}
              className={`w-6 h-6 rounded text-xs font-semibold transition-all ${maxDepth >= d ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'}`}
            >
              {d}
            </button>
          ))}
        </div>

        {/* Period */}
        <div className="ml-auto flex items-center gap-2">
          <Calendar size={14} className="text-zinc-500" />
          <select
            value={selectedPeriod}
            onChange={e => setSelectedPeriod(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-sm text-zinc-300 outline-none cursor-pointer"
          >
            {periods.map(p => (
              <option key={`period-${p}`} value={p}>{p}</option>
            ))}
          </select>
          <button className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors active:scale-95">
            <RefreshCw size={12} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="flex-shrink-0 grid grid-cols-5 gap-3 px-4 py-3 border-b border-zinc-800">
        {[
          { label: 'Network GSV', value: `${(totalGSV/1000).toFixed(1)}K`, sub: 'Apr 2026', icon: TrendingUp, color: 'text-violet-400', trend: '+4.2%' },
          { label: 'Active Distributors', value: `${activeCount}/${allDistributors.length}`, sub: `${Math.round(activeCount/allDistributors.length*100)}% active rate`, icon: Users, color: 'text-emerald-400', trend: '+1' },
          { label: 'Total Commission', value: `$${totalComm.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, sub: 'This period', icon: DollarSign, color: 'text-amber-400', trend: '-1.3%' },
          { label: 'PEXEC At-Risk', value: `${pexecCount}`, sub: 'Need 2,000 GSV', icon: AlertTriangle, color: 'text-orange-400', alert: pexecCount > 0 },
          { label: 'Low PSV Alerts', value: `${atRiskCount}`, sub: 'Below 100 PSV', icon: Activity, color: 'text-red-400', alert: atRiskCount > 0 },
        ].map((kpi) => (
          <div key={`kpi-${kpi.label}`} className={`bg-zinc-900 border ${kpi.alert ? 'border-red-500/30 bg-red-950/10' : 'border-zinc-800'} rounded-xl px-3 py-2.5 flex items-center gap-3`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${kpi.alert ? 'bg-red-500/15' : 'bg-zinc-800'}`}>
              <kpi.icon size={16} className={kpi.color} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 truncate">{kpi.label}</p>
              <p className={`text-lg font-bold tabular-nums font-mono ${kpi.alert ? 'text-red-400' : 'text-zinc-100'}`}>{kpi.value}</p>
              <div className="flex items-center gap-1">
                {kpi.trend && (
                  <span className={`text-[10px] font-medium ${kpi.trend.startsWith('+') ? 'text-emerald-400' : kpi.trend.startsWith('-') ? 'text-red-400' : 'text-zinc-500'}`}>
                    {kpi.trend}
                  </span>
                )}
                <span className="text-[10px] text-zinc-600 truncate">{kpi.sub}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Three-Panel Area */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* LEFT: Network Tree */}
        <div className="w-72 flex-shrink-0 border-r border-zinc-800 overflow-y-auto scrollbar-thin bg-zinc-900/30">
          <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Network Tree</span>
            <span className="text-[10px] text-zinc-600 font-mono">{filteredDistributors.length} nodes</span>
          </div>
          <NetworkTree
            distributors={DISTRIBUTORS}
            rootId="dist-001"
            selectedId={selectedDistributorId}
            onSelect={setSelectedDistributorId}
            maxDepth={maxDepth}
            searchQuery={searchQuery}
            activeFilter={activeFilter}
            selectedRank={selectedRank}
          />
        </div>

        {/* CENTER: Performance Panel */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            <CenterPerformancePanel
              distributor={selectedDist}
              allDistributors={DISTRIBUTORS}
              gsvTrend={GSV_TREND_DATA}
            />
          </div>
          {/* Bottom Center: Generation Panel */}
          <div className="flex-shrink-0 border-t border-zinc-800 h-56 overflow-y-auto scrollbar-thin">
            <GenerationBottomPanel generationData={GENERATION_DATA} />
          </div>
        </div>

        {/* RIGHT: Commission Panel */}
        <div className="w-80 flex-shrink-0 border-l border-zinc-800 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            <CommissionRightPanel distributor={selectedDist} />
          </div>
          {/* Bottom Right: Alerts */}
          <div className="flex-shrink-0 border-t border-zinc-800 h-56 overflow-y-auto scrollbar-thin">
            <AlertsBottomPanel distributors={Object.values(DISTRIBUTORS)} />
          </div>
        </div>
      </div>
    </div>
  );
}